import React, {useEffect, useState} from 'react';

import every from 'lodash/every';
import md5 from 'md5';
import {components, MultiValueRemoveProps} from 'react-select';

import {
  ActionMeta,
  selectStyles,
} from 'app/components/general/SweetReactSelect';
import {useBulkAddTags, useBulkRemoveTags} from 'app/tags/api';
import {TagsSelect} from 'app/tags/components/TagsSelect';
import {gt} from 'app/utils/i18n';

import type {EditTagsDocumentType, NewTagType} from 'app/tags/types';
import type {TaggedItemSerializedType, TagSerializedType} from 'sweet-types';

type Props = {
  canCreateTags: boolean;
  documents: Array<EditTagsDocumentType>;
  onChange: (promise: Promise<any>) => Promise<any>;
  placeholder?: string;
  disabled?: boolean;
  selected: Array<TaggedItemSerializedType>;
};

const sortBy = (
  a: TaggedItemSerializedType,
  b: TaggedItemSerializedType
): number => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1);

export const EditableTagsSelect = ({
  selected,
  canCreateTags,
  placeholder,
  disabled,
  documents,
  onChange,
}: Props) => {
  const [bulkAddTags] = useBulkAddTags();
  const [bulkRemoveTags] = useBulkRemoveTags();

  const customSelectStyles = {
    ...selectStyles,
    valueContainer: (base) => ({
      ...base,
      padding: '4px',
    }),
    multiValue: (base) => ({
      ...base,
      borderRadius: '6px',
    }),
    multiValueRemove: (base) => ({
      ...base,
      marginLeft: '4px',
      cursor: 'pointer',
    }),
  };

  const {data, addItem, removeItem, clearPending} = useOptimisticArray({
    data: selected,
    sortBy,
    createItem: (item: string | TaggedItemSerializedType) =>
      typeof item !== 'string'
        ? item
        : {
            name: item,
            slug: item,
            url: md5(item),
            html_url: 'temp',
          },
    itemId: (item: TaggedItemSerializedType | TagSerializedType) => item.name,
  });

  const onTagCreate = (name: string) => {
    addItem(name);
    onChange(
      bulkAddTags({
        documents: documents.map((document) => ({
          name: name,
          content_type: document.document_content_type,
          object_id: document.object_id,
        })),
      }).unwrap()
    ).catch(() => clearPending({name}));
  };

  const showCreateOption = (
    query: string,
    selectedTags: Array<TaggedItemSerializedType>,
    tags: Array<TaggedItemSerializedType>
  ) =>
    query !== '' &&
    every(tags, (tag) => tag.name !== query) &&
    every(selectedTags, (tag) => tag.name !== query);

  const selectProps: Record<string, any> = {
    classNamePrefix: 'editable-tags',
    formatCreateLabel: (query: string) =>
      gt(
        'Create %s tag',
        <span className="mx-1">
          {'"'}
          <b>{query}</b>
          {'"'}
        </span>
      ),
    onCreateOption: onTagCreate,
    isValidNewOption: showCreateOption,
    isClearable: false,
    noOptionsMessage: () =>
      canCreateTags
        ? gt('No tags found. Enter the tag name to create...')
        : gt('No tags found'),
    styles: customSelectStyles,
  };

  const onTagChange = (
    tags: Array<TagSerializedType | TaggedItemSerializedType | NewTagType>,
    meta: ActionMeta<TagSerializedType | TaggedItemSerializedType>
  ) => {
    if (meta.action == 'remove-value') {
      // eager remove.
      removeItem(meta.removedValue);
      onChange(
        bulkRemoveTags({
          tagged_items: documents
            .flatMap((document) => document.tags)
            .filter((tag) => tag.slug === meta.removedValue.slug)
            .map((tag) => tag.url),
        }).unwrap()
      ).catch(() => clearPending(meta.removedValue));
    } else if (meta.action == 'select-option') {
      addItem({
        ...meta.option,
        html_url: 'temp',
      });
      onChange(
        bulkAddTags({
          documents: documents.map((document) => ({
            name: meta.option.name,
            content_type: document.document_content_type,
            object_id: document.object_id,
          })),
        }).unwrap()
      ).catch(() => clearPending(meta.option));
    }
  };

  return (
    <TagsSelect
      params={{
        exclude_slug: selected.map((t) => t.slug).join(','),
      }}
      placeholder={placeholder != null ? placeholder : gt('Add a tag...')}
      disabled={disabled}
      creatable={canCreateTags}
      selected={data}
      onChange={onTagChange}
      refetchDataOnOpen={false}
      selectProps={selectProps}
      components={{
        LoadingIndicator: null,
        MultiValueRemove: ({
          data,
          ...props
        }: MultiValueRemoveProps<
          TagSerializedType | TaggedItemSerializedType | NewTagType
        >) =>
          data.html_url == 'temp' ? (
            <span className="pl-1 pr-1">
              <i className="fa fa-spinner fa fa-spin" />
            </span>
          ) : (
            <components.MultiValueRemove {...props} />
          ),
      }}
    />
  );
};

type OptimisticProps<T> = {
  data: Array<T>;
  sortBy: (a: T, b: T) => number;
  createItem: (item: string | T) => T;
  itemId: (item: T) => string;
};

function useOptimisticArray<T>({
  data,
  sortBy,
  createItem,
  itemId,
}: OptimisticProps<T>) {
  const [local, setLocal] = useState<Array<T>>(data);
  const [pendingAdd, setPendingAdd] = useState<Array<T>>([]);
  const [pendingRemove, setPendingRemove] = useState<Array<T>>([]);

  const addItem = (item: string | T) => {
    const added = createItem(item);
    const nextPendingAdd = [...pendingAdd, added].sort(sortBy);
    setPendingAdd(nextPendingAdd);
    setLocal(doFilterData(data, nextPendingAdd, pendingRemove));
  };

  const removeItem = (removed) => {
    const nextPendingRemove = [...pendingRemove, removed];
    setPendingRemove(nextPendingRemove);
    setLocal(doFilterData(data, pendingAdd, nextPendingRemove));
  };

  const clearPending = (toClear) => {
    const nextPendingAdd = pendingAdd.filter(
      (item) => itemId(item) !== itemId(toClear)
    );
    setPendingAdd(nextPendingAdd);
    const nextPendingRemove = pendingRemove.filter(
      (item) => itemId(item) !== itemId(toClear)
    );
    setPendingRemove(nextPendingRemove);
    setLocal(doFilterData(data, nextPendingAdd, nextPendingRemove));
  };

  const doFilterData = (data, pendingAdd, pendingRemove) =>
    data
      .filter((item) => !pendingRemove.map(itemId).includes(itemId(item)))
      .concat(
        pendingAdd.filter((item) => !data.map(itemId).includes(itemId(item)))
      )
      .sort(sortBy);

  useEffect(() => {
    // update pending add for things that now appear in 'data'.
    const nextPendingAdd = pendingAdd.filter(
      (item) => !data.map(itemId).includes(itemId(item))
    );
    setPendingAdd(nextPendingAdd);

    // update pending remove for things that don't appear in 'data'.
    const nextPendingRemove = pendingRemove.filter((item) =>
      data.map(itemId).includes(itemId(item))
    );
    setPendingRemove(nextPendingRemove);

    // merges upstream data and pending removed /  added
    setLocal(doFilterData(data, nextPendingAdd, nextPendingRemove));
  }, [data]);

  return {data: local, addItem, removeItem, clearPending};
}
